import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { withPrefix } from 'gatsby'
import { useIntl } from 'react-intl'

type ChatbotManifest = {
  main: string
}

const CHATBOT_Z_INDEX = 1101

const chatbotBundleDir =
  process.env.GATSBY_ACTIVE_ENV === 'production' ? 'production' : 'staging'

function loadScript(src: string): Promise<void> {
  const existing = document.querySelector(
    `script[data-chatbot-bundle="${src}"]`
  )
  if (existing) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.type = 'module'
    script.src = src
    script.async = true
    script.dataset.chatbotBundle = src
    script.onload = () => resolve()
    script.onerror = () =>
      reject(new Error(`Chatbot script failed to load: ${src}`))
    document.body.appendChild(script)
  })
}

type ChatbotWidgetProps = {
  locale?: string
}

export default function ChatbotWidget({
  locale = 'de',
}: ChatbotWidgetProps): ReactElement | null {
  const intl = useIntl()
  const [isReady, setIsReady] = useState(false)

  const translations = useMemo(
    () =>
      JSON.stringify({
        intro: intl.formatMessage({ id: 'chatbot.intro' }),
        placeholder: intl.formatMessage({ id: 'chatbot.placeholder' }),
        labelTrigger: intl.formatMessage({ id: 'chatbot.labelTrigger' }),
        labelReload: intl.formatMessage({ id: 'chatbot.labelReload' }),
        labelSend: intl.formatMessage({ id: 'chatbot.labelSend' }),
      }),
    [intl]
  )

  useEffect(() => {
    let cancelled = false

    async function init() {
      try {
        const manifestUrl = withPrefix(
          `/chatbot/${chatbotBundleDir}/manifest.json`
        )
        const response = await fetch(manifestUrl)

        if (!response.ok) {
          return
        }

        const manifest = (await response.json()) as ChatbotManifest
        if (!manifest.main || cancelled) {
          return
        }

        await loadScript(
          withPrefix(`/chatbot/${chatbotBundleDir}/${manifest.main}`)
        )

        if (!cancelled) {
          setIsReady(true)
        }
      } catch {
        // UI-only: no bundle yet or load failed — widget stays hidden
      }
    }

    init()

    return () => {
      cancelled = true
    }
  }, [])

  if (!isReady) {
    return null
  }

  return (
    <dwc-chat
      ref={(el) => {
        // Beat :host { z-index: 100 } from the web component shadow styles
        el?.style.setProperty('z-index', String(CHATBOT_Z_INDEX))
      }}
      locale={locale}
      translations={translations}
    />
  )
}
